import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { MoreVertical } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/design-system/lib/utils";

export interface RowAction {
  /** Stable identifier — also used as React key. */
  key: string;
  label: string;
  icon?: LucideIcon;
  /** If provided, the row renders as a link (react-router). */
  href?: string;
  /** If provided, clicking fires this callback instead of navigating. */
  onClick?: () => void;
  /** When true, the action is filtered out before render. */
  hidden?: boolean;
  disabled?: boolean;
  destructive?: boolean;
  helperText?: string;
}

export interface RowActionsProps {
  actions: RowAction[];
  label?: string;
  align?: "end" | "start";
  className?: string;
}

/**
 * UX Enhancements §7. Three-dot action menu for the last cell of a DataTable
 * row. Filter out actions via <code>hidden</code> for permission/status gates
 * before passing them in; the component itself doesn't do auth.
 */
export function RowActions({
  actions,
  label = "Row actions",
  align = "end",
  className,
}: RowActionsProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const visible = actions.filter((a) => !a.hidden);
  if (visible.length === 0) return null;

  return (
    <div ref={containerRef} className={cn("relative inline-flex", className)}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          "inline-flex h-8 w-8 items-center justify-center rounded-md",
          "text-gray-mid hover:text-onyx hover:bg-white-warm",
          "focus-visible:outline-none focus-visible:shadow-goldRing"
        )}
      >
        <MoreVertical size={16} strokeWidth={1.5} aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          className={cn(
            "absolute z-30 top-9 min-w-[11rem] rounded-md border border-border-light bg-white-pure shadow-lg",
            align === "end" ? "right-0" : "left-0"
          )}
        >
          <ul className="m-0 p-0 list-none py-space-1">
            {visible.map((a) => (
              <li key={a.key}>
                <MenuItem action={a} onInvoke={() => setOpen(false)} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function MenuItem({ action, onInvoke }: { action: RowAction; onInvoke: () => void }) {
  const Icon = action.icon;
  const content: ReactNode = (
    <span className="flex items-center gap-space-2 flex-1">
      {Icon && <Icon size={14} strokeWidth={1.5} aria-hidden />}
      <span className="flex-1">
        <span className="block text-small">{action.label}</span>
        {action.helperText && (
          <span className="block text-label text-gray-mid">{action.helperText}</span>
        )}
      </span>
    </span>
  );

  const base = cn(
    "w-full flex items-start text-left px-space-3 py-space-2",
    "hover:bg-white-warm",
    action.disabled && "opacity-50 cursor-not-allowed pointer-events-none",
    action.destructive && "text-status-error hover:bg-status-error/10"
  );

  if (action.href && !action.disabled) {
    return (
      <Link
        to={action.href}
        role="menuitem"
        onClick={(e) => {
          e.stopPropagation();
          onInvoke();
        }}
        className={cn(base, "no-underline text-inherit")}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      role="menuitem"
      disabled={action.disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!action.disabled) {
          action.onClick?.();
          onInvoke();
        }
      }}
      className={cn(base, "bg-transparent border-0")}
    >
      {content}
    </button>
  );
}
